import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Flag,
  Plus,
  Settings,
  Users,
  Building2,
  Phone,
  Shield,
  Percent,
  Globe,
  X,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'b2b', label: 'B2B Features' },
  { value: 'b2c', label: 'B2C Features' },
  { value: 'whatsapp', label: 'WhatsApp Features' },
  { value: 'experimental', label: 'Experimental' },
  { value: 'general', label: 'General' },
];

const ROLLOUT_TYPES = [
  { value: 'all', label: 'Everyone', icon: Globe, description: 'Enabled for all users' },
  { value: 'none', label: 'Nobody', icon: X, description: 'Disabled for all users' },
  { value: 'percentage', label: 'Percentage', icon: Percent, description: 'Gradual rollout by %' },
  { value: 'targeted', label: 'Targeted', icon: Users, description: 'Specific users/schools' },
];

const TARGET_TYPES = [
  { value: 'school_ids', label: 'Schools', icon: Building2, placeholder: 'Enter school ID' },
  { value: 'user_ids', label: 'Users', icon: Users, placeholder: 'Enter user ID' },
  { value: 'phone_numbers', label: 'Phone Numbers', icon: Phone, placeholder: 'Enter phone number' },
  { value: 'roles', label: 'Roles', icon: Shield, placeholder: 'Select role' },
  { value: 'subscriber_ids', label: 'Subscribers', icon: Users, placeholder: 'Enter subscriber ID' },
  { value: 'student_ids', label: 'Students', icon: Users, placeholder: 'Enter student ID' },
];

const ROLES = ['admin', 'teacher', 'parent', 'student', 'school_admin'];

export default function AdminFeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [expandedFlags, setExpandedFlags] = useState({});
  const { toast } = useToast();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      
      const response = await fetch(`${API_URL}/api/admin/feature-flags?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to fetch feature flags');
      
      const data = await response.json();
      setFlags(data.flags || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, toast]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const toggleFlag = async (flagKey, enabled) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/feature-flags/${flagKey}/toggle`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ enabled }),
      });
      
      if (!response.ok) throw new Error('Failed to toggle flag');
      
      toast({
        title: 'Success',
        description: `Feature flag ${enabled ? 'enabled' : 'disabled'}`,
      });
      
      fetchFlags();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const updateFlag = async (flagKey, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/feature-flags/${flagKey}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      
      if (!response.ok) throw new Error('Failed to update flag');
      
      toast({
        title: 'Success',
        description: 'Feature flag updated',
      });
      
      setEditDialogOpen(false);
      fetchFlags();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const createFlag = async (flagData) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/feature-flags`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(flagData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create flag');
      }
      
      toast({
        title: 'Success',
        description: 'Feature flag created',
      });
      
      setCreateDialogOpen(false);
      fetchFlags();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const addTarget = async (flagKey, targetType, targetValue) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/feature-flags/${flagKey}/targets`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ target_type: targetType, target_value: targetValue }),
      });
      
      if (!response.ok) throw new Error('Failed to add target');
      
      toast({
        title: 'Success',
        description: 'Target added',
      });
      
      fetchFlags();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const removeTarget = async (flagKey, targetType, targetValue) => {
    try {
      const params = new URLSearchParams({
        target_type: targetType,
        target_value: targetValue,
      });
      
      const response = await fetch(`${API_URL}/api/admin/feature-flags/${flagKey}/targets?${params}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to remove target');
      
      toast({
        title: 'Success',
        description: 'Target removed',
      });
      
      fetchFlags();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const filteredFlags = flags.filter(flag => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        flag.flag_key.toLowerCase().includes(search) ||
        flag.name.toLowerCase().includes(search) ||
        flag.description?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const getCategoryColor = (category) => {
    const colors = {
      b2b: 'bg-blue-100 text-blue-700',
      b2c: 'bg-green-100 text-green-700',
      whatsapp: 'bg-emerald-100 text-emerald-700',
      experimental: 'bg-purple-100 text-purple-700',
      general: 'bg-gray-100 text-gray-700',
    };
    return colors[category] || colors.general;
  };

  const getRolloutBadge = (rollout) => {
    const type = rollout?.type || 'none';
    const config = {
      all: { label: 'Everyone', color: 'bg-green-100 text-green-700' },
      none: { label: 'Nobody', color: 'bg-red-100 text-red-700' },
      percentage: { label: `${rollout?.percentage || 0}%`, color: 'bg-yellow-100 text-yellow-700' },
      targeted: { label: 'Targeted', color: 'bg-blue-100 text-blue-700' },
    };
    return config[type] || config.none;
  };

  const toggleExpanded = (flagKey) => {
    setExpandedFlags(prev => ({
      ...prev,
      [flagKey]: !prev[flagKey],
    }));
  };

  return (
    <div className="p-6 space-y-6" data-testid="feature-flags-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Flag className="h-6 w-6" />
            Feature Flags
          </h1>
          <p className="text-gray-500 mt-1">Control feature rollout across B2B and B2C</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchFlags} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Flag
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search flags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Flags List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              Loading feature flags...
            </CardContent>
          </Card>
        ) : filteredFlags.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              No feature flags found
            </CardContent>
          </Card>
        ) : (
          filteredFlags.map(flag => (
            <Card key={flag.flag_key} data-testid={`flag-${flag.flag_key}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg">{flag.name}</h3>
                      <Badge className={getCategoryColor(flag.category)}>
                        {flag.category}
                      </Badge>
                      <Badge className={getRolloutBadge(flag.rollout).color}>
                        {getRolloutBadge(flag.rollout).label}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{flag.description}</p>
                    <code className="text-xs text-gray-400 mt-1 block">{flag.flag_key}</code>
                  </div>
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={flag.enabled}
                      onCheckedChange={(checked) => toggleFlag(flag.flag_key, checked)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(flag.flag_key)}
                    >
                      {expandedFlags[flag.flag_key] ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedFlag(flag);
                        setEditDialogOpen(true);
                      }}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Targets View */}
                {expandedFlags[flag.flag_key] && flag.rollout?.type === 'targeted' && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-3">Targeted Rollout</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {TARGET_TYPES.map(targetType => {
                        const targets = flag.rollout?.targets?.[targetType.value] || [];
                        const Icon = targetType.icon;
                        return (
                          <div key={targetType.value} className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                              <Icon className="h-4 w-4" />
                              {targetType.label}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {targets.length === 0 ? (
                                <span className="text-xs text-gray-400">None</span>
                              ) : (
                                targets.map(target => (
                                  <Badge
                                    key={target}
                                    variant="secondary"
                                    className="text-xs cursor-pointer hover:bg-red-100"
                                    onClick={() => removeTarget(flag.flag_key, targetType.value, target)}
                                  >
                                    {target}
                                    <X className="h-3 w-3 ml-1" />
                                  </Badge>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <EditFlagDialog
        flag={selectedFlag}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={updateFlag}
        onAddTarget={addTarget}
      />

      {/* Create Dialog */}
      <CreateFlagDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={createFlag}
      />
    </div>
  );
}

function EditFlagDialog({ flag, open, onClose, onSave, onAddTarget }) {
  const [rolloutType, setRolloutType] = useState('none');
  const [percentage, setPercentage] = useState(0);
  const [newTargetType, setNewTargetType] = useState('school_ids');
  const [newTargetValue, setNewTargetValue] = useState('');

  useEffect(() => {
    if (flag) {
      setRolloutType(flag.rollout?.type || 'none');
      setPercentage(flag.rollout?.percentage || 0);
    }
  }, [flag]);

  if (!flag) return null;

  const handleSave = () => {
    const updates = {
      rollout: {
        type: rolloutType,
        percentage: rolloutType === 'percentage' ? percentage : null,
        targets: flag.rollout?.targets || {},
        exclusions: flag.rollout?.exclusions || {},
      },
    };
    onSave(flag.flag_key, updates);
  };

  const handleAddTarget = () => {
    if (newTargetValue.trim()) {
      onAddTarget(flag.flag_key, newTargetType, newTargetValue.trim());
      setNewTargetValue('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Feature Flag: {flag.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Rollout Type */}
          <div className="space-y-3">
            <Label>Rollout Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {ROLLOUT_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <div
                    key={type.value}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      rolloutType === type.value
                        ? 'border-primary bg-primary/5'
                        : 'hover:border-gray-300'
                    }`}
                    onClick={() => setRolloutType(type.value)}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{type.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Percentage Slider */}
          {rolloutType === 'percentage' && (
            <div className="space-y-3">
              <Label>Rollout Percentage: {percentage}%</Label>
              <input
                type="range"
                min="0"
                max="100"
                value={percentage}
                onChange={(e) => setPercentage(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          )}

          {/* Add Targets */}
          {rolloutType === 'targeted' && (
            <div className="space-y-3">
              <Label>Add Target</Label>
              <div className="flex gap-2">
                <Select value={newTargetType} onValueChange={setNewTargetType}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TARGET_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newTargetType === 'roles' ? (
                  <Select value={newTargetValue} onValueChange={setNewTargetValue}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map(role => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder={TARGET_TYPES.find(t => t.value === newTargetType)?.placeholder}
                    value={newTargetValue}
                    onChange={(e) => setNewTargetValue(e.target.value)}
                    className="flex-1"
                  />
                )}
                <Button onClick={handleAddTarget}>Add</Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateFlagDialog({ open, onClose, onCreate }) {
  const [formData, setFormData] = useState({
    flag_key: '',
    name: '',
    description: '',
    category: 'general',
    enabled: false,
  });

  const handleCreate = () => {
    if (!formData.flag_key || !formData.name) return;
    onCreate({
      ...formData,
      rollout: { type: 'none' },
    });
    setFormData({
      flag_key: '',
      name: '',
      description: '',
      category: 'general',
      enabled: false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Feature Flag</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Flag Key (unique identifier)</Label>
            <Input
              placeholder="e.g., b2b_teacher_chat"
              value={formData.flag_key}
              onChange={(e) => setFormData({ ...formData, flag_key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            />
          </div>

          <div className="space-y-2">
            <Label>Display Name</Label>
            <Input
              placeholder="e.g., B2B Teacher Chat"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              placeholder="What does this feature do?"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label>Start Enabled</Label>
            <Switch
              checked={formData.enabled}
              onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!formData.flag_key || !formData.name}>
            Create Flag
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
